import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import {
  applyContractShortcut,
  onGatePassChange,
  buildPurchaseContractBatchPayload,
  buildPurchaseContractPayload,
  buildQuickPresetBatchPayload,
  countQuickPresetItemLines,
  emptyPurchaseContractBatchForm,
  emptyPurchaseContractForm,
  FARMER_ARRIVAL_CONTRACT_SHORTCUTS,
  SUPPLIER_ARRIVAL_CONTRACT_SHORTCUTS,
  isArrivalPhase,
  gatePassSetsFarmer,
  isOnArrivalDirectPurchase,
  isPreArrivalPhase,
  isSupplierArrivalShortcut,
  labelFor,
  paymentLabel,
  partyLabel,
  phaseLabel,
  purchaseTypesForPhase,
} from "../lib/purchaseContractOptions";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Plus, X, Zap } from "lucide-react";
import PurchaseContractDetailDialog from "../components/PurchaseContractDetailDialog";
import AdminEntityActions from "../components/AdminEntityActions";
import PurchaseContractFormFields from "../components/PurchaseContractFormFields";
import PurchaseContractItemLines from "../components/PurchaseContractItemLines";
import PurchaseContractSampleLink from "../components/PurchaseContractSampleLink";
import DataTableBlock, { dataTableListTitle } from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useBootstrapList } from "../hooks/useBootstrapList";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { CONTRACT_COLUMNS } from "./contractColumns";

const CONTRACTS_WS_TYPES = ["contract", "purchase"];

export default function ContractsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const list = useBootstrapList({
    path: "/purchase-contracts/bootstrap",
    columns: CONTRACT_COLUMNS,
    defaultSort: { id: "contract_date", desc: true },
    wsTypes: CONTRACTS_WS_TYPES,
  });
  useBootstrapWS(CONTRACTS_WS_TYPES, list.reload);
  const partyPickers = useMemo(
    () => ({
      brokers: list.extras.brokers ?? [],
      farmers: list.extras.farmers ?? [],
      suppliers: list.extras.suppliers ?? [],
    }),
    [list.extras.brokers, list.extras.farmers, list.extras.suppliers],
  );
  const [open, setOpen] = useState(false);
  const [formMode, setFormMode] = useState("single");
  const [detailId, setDetailId] = useState(() => {
    const raw = searchParams.get("contract");
    return raw ? Number(raw) || raw : null;
  });
  const [form, setForm] = useState(emptyPurchaseContractForm);
  const [batchForm, setBatchForm] = useState(emptyPurchaseContractBatchForm);
  const [submitting, setSubmitting] = useState(false);
  const [linkableLabs, setLinkableLabs] = useState([]);
  const [linkableGatePasses, setLinkableGatePasses] = useState([]);
  const [quickPresetId, setQuickPresetId] = useState(null);

  useEffect(() => {
    const raw = searchParams.get("contract");
    if (!raw) return;
    setDetailId(Number(raw) || raw);
  }, [searchParams]);

  useEffect(() => {
    const current = searchParams.get("contract") || "";
    const nextId = detailId != null ? String(detailId) : "";
    if (current === nextId) return;
    const next = new URLSearchParams(searchParams);
    if (detailId != null) next.set("contract", String(detailId));
    else next.delete("contract");
    setSearchParams(next, { replace: true });
  }, [detailId, searchParams, setSearchParams]);

  const activeForm = formMode === "multi" ? batchForm : form;
  const setActiveForm = formMode === "multi" ? setBatchForm : setForm;
  const isArrival = isArrivalPhase(activeForm.contract_phase);
  const isPreArrival = isPreArrivalPhase(activeForm.contract_phase);
  const onArrivalDirect = isOnArrivalDirectPurchase(activeForm);
  const isStock = activeForm.purchase_type === "STOCK_PURCHASE";
  const sampleAnalysed = activeForm.stock_sample_analysed === "yes";

  useEffect(() => {
    if (!open || !isArrival) {
      setLinkableGatePasses([]);
      return;
    }
    api
      .get("/purchase-contracts/linkable-gate-passes", {})
      .then((r) => setLinkableGatePasses(r.data))
      .catch(() => setLinkableGatePasses([]));
  }, [open, isArrival]);

  useEffect(() => {
    if (!open || formMode !== "single" || !form.gate_pass_id) return;
    if (linkableGatePasses.some((g) => String(g.id) === String(form.gate_pass_id))) return;
    setForm((f) => onGatePassChange(f, "", linkableGatePasses));
  }, [linkableGatePasses, open, formMode, form.gate_pass_id]);

  useEffect(() => {
    if (!open || !isPreArrival || !isStock || !sampleAnalysed) {
      setLinkableLabs([]);
      return;
    }
    api
      .get("/purchase-contracts/linkable-stack-lab-reports")
      .then((r) => setLinkableLabs(r.data))
      .catch(() => setLinkableLabs([]));
  }, [open, isPreArrival, isStock, sampleAnalysed]);

  const validateShared = () => {
    const farmerFromGate = gatePassSetsFarmer(activeForm) || Boolean(quickPresetId);
    if (isArrival && !activeForm.gate_pass_id) {
      toast.error(farmerFromGate ? "Select the truck's gate pass in yard" : "Select a gate pass for arrival contracts");
      return false;
    }
    if (farmerFromGate) {
      if (!activeForm.party_id) {
        toast.error("Selected gate pass has no party — link a farmer or supplier on the gate pass first");
        return false;
      }
    } else if (!activeForm.party_id) {
      toast.error("Select a farmer or supplier");
      return false;
    }
    if (isPreArrival && isStock && !activeForm.stock_sample_analysed) {
      toast.error("Please indicate if the stock sample is already analysed");
      return false;
    }
    if (isPreArrival && isStock && sampleAnalysed && !activeForm.stack_sample_lab_report_id) {
      toast.error("Select a stack sample lab report to link");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateShared()) return;

    if (quickPresetId) {
      const lines = form.items || [];
      const filledCount = countQuickPresetItemLines(form);
      if (filledCount === 0) {
        toast.error("Add at least one variety with item and rate");
        return;
      }
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const hasQuality = String(line.quality || "").trim();
        const hasPrice = line.price !== "" && line.price != null;
        if (hasQuality && !hasPrice) {
          toast.error(`Line ${i + 1}: rate is required`);
          return;
        }
        if (hasPrice && !hasQuality) {
          toast.error(`Line ${i + 1}: select quality / item`);
          return;
        }
      }
      try {
        setSubmitting(true);
        const res = await api.post("/purchase-contracts/batch", buildQuickPresetBatchPayload(form));
        const nums = res.data?.contract_numbers || [];
        toast.success(
          nums.length
            ? `Created ${nums.length} contracts: ${nums.join(", ")}`
            : `Created ${filledCount} purchase contracts`,
        );
        setOpen(false);
        setForm(emptyPurchaseContractForm());
        setQuickPresetId(null);
        list.reload();
      } catch (err) {
        toast.error(extractError(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (formMode === "multi") {
      const lines = batchForm.items || [];
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (!String(line.quality || "").trim()) {
          toast.error(`Line ${i + 1}: select quality / variety`);
          return;
        }
        if (!onArrivalDirect && !line.quantity) {
          toast.error(`Line ${i + 1}: quantity is required`);
          return;
        }
        if (!line.price) {
          toast.error(`Line ${i + 1}: rate is required`);
          return;
        }
      }
      try {
        setSubmitting(true);
        const res = await api.post("/purchase-contracts/batch", buildPurchaseContractBatchPayload(batchForm));
        const nums = res.data?.contract_numbers || [];
        toast.success(
          nums.length
            ? `Created ${nums.length} contracts: ${nums.join(", ")}`
            : `Created ${res.data?.count || lines.length} purchase contracts`,
        );
        setOpen(false);
        setBatchForm(emptyPurchaseContractBatchForm());
        setFormMode("single");
        list.reload();
      } catch (err) {
        toast.error(extractError(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (onArrivalDirect && !form.quality) {
      const fromLines = countQuickPresetItemLines(form);
      if (!fromLines) {
        toast.error("Select quality / item for on-arrival direct purchase");
        return;
      }
    }
    if (!onArrivalDirect && !form.quantity) {
      toast.error("Quantity is required");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/purchase-contracts", buildPurchaseContractPayload(form));
      toast.success(
        isPreArrival && isStock && !sampleAnalysed
          ? "Contract saved — sent to supervisor TODO for sample intake"
          : `${phaseLabel(form.contract_phase)} purchase contract created`,
      );
      setOpen(false);
      setForm(emptyPurchaseContractForm());
      setQuickPresetId(null);
      list.reload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openNewForm = (mode) => {
    setFormMode(mode);
    setQuickPresetId(null);
    if (mode === "multi") {
      setBatchForm(emptyPurchaseContractBatchForm());
    } else {
      setForm(emptyPurchaseContractForm());
    }
    setOpen(true);
  };

  const applyShortcut = (shortcutId) => {
    const preserve = formMode === "single" && open ? form : {};
    setFormMode("single");
    setQuickPresetId(shortcutId);
    setForm(applyContractShortcut(shortcutId, preserve));
    setOpen(true);
    const sc = [...FARMER_ARRIVAL_CONTRACT_SHORTCUTS, ...SUPPLIER_ARRIVAL_CONTRACT_SHORTCUTS].find(
      (s) => s.id === shortcutId,
    );
    toast.success(sc ? `Preset: ${sc.label}` : "Preset applied");
  };

  const clearQuickPreset = () => {
    setQuickPresetId(null);
    toast.message("Preset removed — all fields shown");
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="contracts-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Purchase Contracts"
        description="Register pre-arrival or arrival purchase contracts. Use multiple items when one party/deal has 2–3 varieties (separate contract per line; same truck at weighbridge)."
        actions={
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => openNewForm("single")}
              variant={open && formMode === "single" ? "default" : "outline"}
              className={open && formMode === "single" ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}
              data-testid="new-purchase-contract-button"
            >
              <Plus className="h-4 w-4 mr-1" /> Single item
            </Button>
            <Button
              onClick={() => openNewForm("multi")}
              variant={open && formMode === "multi" ? "default" : "outline"}
              className={open && formMode === "multi" ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}
              data-testid="new-multi-purchase-contract-button"
            >
              <Plus className="h-4 w-4 mr-1" /> Multiple items
            </Button>
          </div>
        }
      />


      <SectionCard title="Quick create — farmer arrival">
        <p className="text-sm text-muted-foreground mb-3">
          Fills arrival, on-arrival direct, loose bardana, 1 kg/qtl deduction, and FOR delivery. Pick the farmer truck in yard, then one or more varieties with rate (one contract per line).
        </p>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-testid="pac-shortcuts">
          {FARMER_ARRIVAL_CONTRACT_SHORTCUTS.map((sc) => (
            <button
              key={sc.id}
              type="button"
              onClick={() => applyShortcut(sc.id)}
              className="flex-1 min-w-[240px] text-left rounded-lg border-2 border-teal-200 bg-teal-50/50 hover:bg-teal-50 hover:border-teal-400 px-4 py-3 transition-colors"
              data-testid={`pac-shortcut-${sc.id}`}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-teal-900">
                <Zap className="h-4 w-4 shrink-0 text-teal-600" />
                Farmer · {sc.label}
              </span>
              <span className="block text-xs text-muted-foreground mt-1">{sc.description}</span>
            </button>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Quick create — supplier arrival">
        <p className="text-sm text-muted-foreground mb-3">
          Same on-arrival direct flow for supplier trucks (e.g. GP-260723-0001). Gate pass party must be a supplier.
        </p>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-testid="pac-supplier-shortcuts">
          {SUPPLIER_ARRIVAL_CONTRACT_SHORTCUTS.map((sc) => (
            <button
              key={sc.id}
              type="button"
              onClick={() => applyShortcut(sc.id)}
              className="flex-1 min-w-[240px] text-left rounded-lg border-2 border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50 hover:border-emerald-400 px-4 py-3 transition-colors"
              data-testid={`pac-shortcut-${sc.id}`}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                <Zap className="h-4 w-4 shrink-0 text-emerald-600" />
                Supplier · {sc.label}
              </span>
              <span className="block text-xs text-muted-foreground mt-1">{sc.description}</span>
            </button>
          ))}
        </div>
      </SectionCard>

      {open && (
        <SectionCard
          title={
            formMode === "multi"
              ? "New purchase contracts (multiple items)"
              : quickPresetId
                ? `Quick create — ${
                    [...FARMER_ARRIVAL_CONTRACT_SHORTCUTS, ...SUPPLIER_ARRIVAL_CONTRACT_SHORTCUTS].find(
                      (s) => s.id === quickPresetId,
                    )?.label
                    || (isSupplierArrivalShortcut(quickPresetId) ? "supplier arrival" : "farmer arrival")
                  }`
                : "New purchase contract"
          }
        >
          {formMode === "single" && quickPresetId && (
            <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b" data-testid="pac-shortcuts-inline">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground w-full sm:w-auto sm:mr-2 self-center">
                Switch preset
              </span>
              {(isSupplierArrivalShortcut(quickPresetId)
                ? SUPPLIER_ARRIVAL_CONTRACT_SHORTCUTS
                : FARMER_ARRIVAL_CONTRACT_SHORTCUTS
              ).map((sc) => (
                <Button
                  key={sc.id}
                  type="button"
                  variant={quickPresetId === sc.id ? "default" : "outline"}
                  size="sm"
                  className={
                    quickPresetId === sc.id
                      ? "bg-bansal-sage hover:bg-bansal-sageDark text-white"
                      : isSupplierArrivalShortcut(quickPresetId)
                        ? "border-emerald-300 text-emerald-900 hover:bg-emerald-50"
                        : "border-teal-300 text-teal-900 hover:bg-teal-50"
                  }
                  onClick={() => applyShortcut(sc.id)}
                  data-testid={`pac-shortcut-inline-${sc.id}`}
                >
                  <Zap className="h-3.5 w-3.5 mr-1" />
                  {sc.label}
                </Button>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-border text-muted-foreground hover:bg-muted/50"
                onClick={clearQuickPreset}
                data-testid="pac-remove-preset"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Remove preset
              </Button>
            </div>
          )}
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="purchase-contract-form">
            <PurchaseContractFormFields
              form={formMode === "multi" ? batchForm : form}
              setForm={formMode === "multi" ? setBatchForm : setForm}
              brokers={partyPickers.brokers}
              farmers={partyPickers.farmers}
              suppliers={partyPickers.suppliers}
              linkableLabs={linkableLabs}
              linkableGatePasses={linkableGatePasses}
              itemFieldsMode={formMode === "multi" ? "shared" : "full"}
              quickPresetId={formMode === "single" ? quickPresetId : null}
              onClearQuickPreset={clearQuickPreset}
            />
            {(formMode === "multi" || quickPresetId) && (
              <PurchaseContractItemLines
                form={quickPresetId ? form : batchForm}
                setForm={quickPresetId ? setForm : setBatchForm}
                linkableGatePasses={linkableGatePasses}
                compact={Boolean(quickPresetId)}
              />
            )}
            <div className="md:col-span-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto min-h-[44px]"
                onClick={() => {
                  setOpen(false);
                  setFormMode("single");
                  setQuickPresetId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full sm:w-auto min-h-[44px]"
                data-testid="pac-submit"
              >
                {submitting
                  ? "Saving…"
                  : quickPresetId
                    ? (() => {
                        const n = countQuickPresetItemLines(form) || (form.items || []).length;
                        return `Register ${n} contract${n === 1 ? "" : "s"}`;
                      })()
                    : formMode === "multi"
                      ? `Register ${(batchForm.items || []).length} contracts`
                      : "Register contract"}
              </Button>
            </div>
          </form>
        </SectionCard>
      )}

      <DataTableBlock
        title={dataTableListTitle("contracts", list.meta.count, {
          filtered: list.table.rows.length,
          hasFilters: list.table.hasActiveFilters,
        })}
        columns={CONTRACT_COLUMNS}
        table={list.table}
        loading={list.loading}
        totalCount={list.meta.count}
        testId="purchase-contracts-table"
        minWidth="1280px"
        emptyMessage="No purchase contracts yet."
        emptyFilteredMessage="No contracts match your filters."
        pagination={{ ...list.pagination, pageSizeOptions: LIST_PAGE_SIZE_OPTIONS }}
        exportButton={(
          <DataTableExportButton
            apiPath="/purchase-contracts"
            params={list.exportParams}
            columns={CONTRACT_COLUMNS}
            filename="purchase-contracts.xlsx"
            testId="purchase-contracts-export"
          />
        )}
        renderTrailingHeader=""
        renderRow={(c) => (
          <tr key={c.id} className="border-t hover:bg-accent/60" data-testid={`pac-row-${c.contract_number}`}>
            <td className="px-3 py-2.5 font-mono text-xs">
              <button
                type="button"
                className="text-primary hover:text-primary underline underline-offset-2 font-medium"
                onClick={() => setDetailId(c.id)}
                data-testid={`pac-open-${c.contract_number}`}
              >
                {c.contract_number}
              </button>
            </td>
            <td className="px-3 py-2.5 text-xs">
              <Pill tone={c.contract_phase === "ARRIVAL" ? "sage" : "gold"}>
                {c.contract_phase_label || phaseLabel(c.contract_phase)}
              </Pill>
            </td>
            <td className="px-3 py-2.5 text-xs">{c.contract_date}</td>
            <td className="px-3 py-2.5 text-xs">{labelFor(purchaseTypesForPhase(c.contract_phase), c.purchase_type)}</td>
            <td className="px-3 py-2.5 text-xs">{partyLabel(c)}</td>
            <td className="px-3 py-2.5 font-mono text-xs">{c.gate_pass_code || "—"}</td>
            <td className="px-3 py-2.5">{c.quality}</td>
            <td className="px-3 py-2.5 tabular">
              {c.purchase_type === "ON_ARRIVAL_DIRECT_PURCHASE" ? (
                "—"
              ) : (
                <span>
                  {c.received_qtl != null ? `${c.received_qtl} / ` : ""}
                  {c.quantity}
                  {c.reserved_qtl > 0 ? ` + ${c.reserved_qtl} reserved` : ""}
                  {c.balance_qtl != null ? ` · bal ${c.balance_qtl}` : ""}
                </span>
              )}
            </td>
            <td className="px-3 py-2.5 tabular">₹{c.price}</td>
            <td className="px-3 py-2.5 text-xs">{paymentLabel(c)}</td>
            <td className="px-3 py-2.5 text-xs">
              <PurchaseContractSampleLink contract={c} />
            </td>
            <td className="px-3 py-2.5 text-xs">
              {c.broker_applicable ? (
                <>
                  <span className="font-medium">{c.broker_party_name || c.broker_name}</span>
                  <span className="block text-muted-foreground">₹{c.brokerage}</span>
                </>
              ) : (
                "—"
              )}
            </td>
            <td className="px-3 py-2.5">
              <Pill tone={c.status === "CLOSED" ? "muted" : "sage"}>{c.status}</Pill>
            </td>
            <td className="px-3 py-2.5">
              <AdminEntityActions
                entityType="purchase_contract"
                entityId={c.id}
                entityLabel={c.contract_number}
                onEdit={() => setDetailId(c.id)}
                onDeleted={list.reload}
              />
            </td>
          </tr>
        )}
      />

      <PurchaseContractDetailDialog
        contractId={detailId}
        open={Boolean(detailId)}
        onOpenChange={(v) => { if (!v) setDetailId(null); }}
        onSaved={list.reload}
        onDeleted={list.reload}
        partyPickers={partyPickers}
      />
    </div>
  );
}
